import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ResponsiveLine } from "@nivo/line";
import { tokens } from "../../theme/theme";
import { fmtDate } from "./opportunityFormat";

/** Palette mit dem Validator der Dataviz-Vorgaben geprüft, je Modus gegen die Kartenfläche. */
export function seriesColors(mode) {
    return mode === 'dark'
        ? { primary: '#3987e5', secondary: '#d95926' }
        : { primary: '#2a78d6', secondary: '#eb6834' };
}

function xAxisFor(days, narrow) {
    const years = days.length > 1
        ? (Date.parse(days[days.length - 1]) - Date.parse(days[0])) / (365.25 * 86400000)
        : 0;
    if (years > 8) return { tickValues: narrow ? 'every 4 years' : 'every 2 years', format: '%Y' };
    if (years > 3) return { tickValues: narrow ? 'every 2 years' : 'every year', format: '%Y' };
    return { tickValues: narrow ? 3 : 6, format: '%m/%y' };
}

/** Linien nur für Reihen mit showLine. So entsteht zwischen Rückblick und Wochenläufen keine falsche Verbindung. */
function LinesLayer({ series, lineGenerator }) {
    return series.filter((serie) => serie.showLine !== false).map((serie) => (
        <path
            key={serie.id}
            d={lineGenerator(serie.data.map((point) => point.position))}
            fill="none"
            stroke={serie.color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
        />
    ));
}

function PointsLayer({ series, currentPoint }) {
    return series.flatMap((serie) => serie.data
        .filter((point) => point.position.x !== null && point.position.y !== null)
        .map((point, index) => {
            const isCurrent = currentPoint?.serieId === serie.id && Number(currentPoint.data.x) === Number(point.data.x);
            // Auch Reihen ohne eigene Punkte zeigen beim Überfahren den aktuellen Punkt.
            const radius = isCurrent ? Math.max(serie.pointRadius, 3) + 2 : serie.pointRadius;
            if (radius <= 0) return null;
            return (
                <circle
                    key={`${serie.id}-${index}`}
                    cx={point.position.x}
                    cy={point.position.y}
                    r={radius}
                    fill={serie.color}
                    stroke={radius > 3 ? serie.ringColor : 'none'}
                    strokeWidth={2}
                />
            );
        }));
}

/**
 * Zeitreihe auf einer Achse mit eigenem Tooltip.
 * series: [{ id, color, data: [{ x: 'JJJJ-MM-TT', y, ... }], showLine, pointRadius }]
 * markers: Bezugslinien, z. B. eigener Median
 */
export function TimeChart({ series, markers = [], yFormat, yMin = 'auto', height = 260, renderTooltip, ariaLabel }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const narrow = useMediaQuery(theme.breakpoints.down('sm'));
    const surface = theme.palette.background.paper;
    const days = series.flatMap((serie) => serie.data.map((point) => point.x)).sort();
    const axis = xAxisFor(days, narrow);
    const ink = colors.grey[300];

    const nivoTheme = {
        textColor: ink,
        axis: {
            ticks: { text: { fill: ink, fontSize: 11 }, line: { stroke: 'transparent' } },
            domain: { line: { stroke: 'transparent' } },
        },
        grid: { line: { stroke: theme.palette.divider, strokeWidth: 1 } },
        crosshair: { line: { stroke: ink, strokeWidth: 1, strokeOpacity: 0.5 } },
        markers: { text: { fill: ink, fontSize: 11 } },
    };

    const data = series.map((serie) => ({ ringColor: surface, pointRadius: 0, ...serie }));

    return (
        <Box role="img" aria-label={ariaLabel} sx={{ height, width: '100%', minWidth: 0 }}>
            <ResponsiveLine
                data={data}
                theme={nivoTheme}
                colors={(serie) => serie.color}
                margin={{ top: 12, right: narrow ? 12 : 24, bottom: 28, left: 48 }}
                xScale={{ type: 'time', format: '%Y-%m-%d', useUTC: false, precision: 'day' }}
                xFormat="time:%Y-%m-%d"
                yScale={{ type: 'linear', min: yMin, max: 'auto' }}
                axisBottom={{ format: axis.format, tickValues: axis.tickValues, tickSize: 0, tickPadding: 8 }}
                axisLeft={{ format: yFormat, tickValues: 5, tickSize: 0, tickPadding: 8 }}
                gridXValues={[]}
                gridYValues={5}
                enablePoints={false}
                useMesh
                enableCrosshair
                crosshairType="x"
                markers={markers.map((marker) => ({
                    axis: 'y',
                    value: marker.value,
                    legend: marker.label,
                    legendPosition: 'top-right',
                    lineStyle: { stroke: ink, strokeWidth: 1, strokeDasharray: '4 4' },
                    textStyle: { fill: ink, fontSize: 11 },
                }))}
                layers={['grid', 'markers', 'axes', LinesLayer, 'crosshair', PointsLayer, 'mesh']}
                tooltip={({ point }) => (
                    <Box sx={{
                        backgroundColor: surface,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        px: 1.25,
                        py: 0.75,
                        boxShadow: 3,
                        whiteSpace: 'nowrap',
                    }}>
                        <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }}>
                            {fmtDate(point.data.xFormatted)}
                        </Typography>
                        {renderTooltip(point)}
                    </Box>
                )}
            />
        </Box>
    );
}

/** Legende bleibt sichtbar, damit die Reihen nicht allein über die Farbe unterscheidbar sind. */
export function ChartLegend({ items }) {
    return (
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 2, mb: 0.5 }}>
            {items.map((item) => (
                <Stack key={item.label} direction="row" spacing={0.75} alignItems="center">
                    {item.kind === 'dot' && (
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: item.color }} />
                    )}
                    {item.kind === 'line' && (
                        <Box sx={{ width: 18, height: 2, borderRadius: 1, backgroundColor: item.color }} />
                    )}
                    {item.kind === 'dash' && (
                        <Box sx={{ width: 18, borderTop: `1px dashed ${item.color}` }} />
                    )}
                    <Typography variant="caption">{item.label}</Typography>
                </Stack>
            ))}
        </Stack>
    );
}
